import type { ConcessionRecord } from '@avisoma/shared';
import { Badge } from '../../catalyst/badge';

type Role = NonNullable<ConcessionRecord['role']>;
type Field = { key: keyof ConcessionRecord; label: string; wide?: boolean };

const ROLE_COLORS: Record<Role, 'zinc' | 'blue' | 'indigo' | 'amber' | 'green'> = {
  reference: 'zinc',
  concession: 'blue',
  part: 'indigo',
  limitation: 'amber',
  approval: 'green',
  nil: 'zinc',
};

const VALUE_LABELS: Record<string, string> = {
  major: 'Major',
  minor: 'Minor',
  repair: 'Repair',
  alteration: 'Alteration',
  'life-limit': 'Life limit',
  'inspection-threshold': 'Inspection threshold',
  engine: 'Engine',
  part: 'Part',
};

const FIELD_GROUPS: ReadonlyArray<ReadonlyArray<Field>> = [
  [
    { key: 'concessionNumber', label: 'Concession Number' },
    { key: 'registryNumber', label: 'Registry Number' },
    { key: 'designApprovalRef', label: 'Design Approval Ref' },
    { key: 'certificateNumber', label: 'Certificate Number' },
  ],
  [
    { key: 'repairClass', label: 'Repair Class' },
    { key: 'repairType', label: 'Repair Type' },
    { key: 'approved', label: 'Approved' },
  ],
  [
    { key: 'partNumber', label: 'Part Number' },
    { key: 'serialNumber', label: 'Serial Number' },
    { key: 'partDescription', label: 'Part Description' },
  ],
  [
    { key: 'limitKind', label: 'Limit Kind' },
    { key: 'limitSubject', label: 'Limit Applies To' },
    { key: 'newLifeLimitCycles', label: 'New Life Limit (cycles)' },
    { key: 'inspectionThresholdCycles', label: 'Inspection Threshold (cycles)' },
    { key: 'affectedRating', label: 'Affected Rating' },
    { key: 'limitationText', label: 'Limitation', wide: true },
  ],
  [
    { key: 'issuer', label: 'Issuer' },
    { key: 'operator', label: 'Operator' },
    { key: 'facility', label: 'Facility' },
    { key: 'shopVisitDate', label: 'Shop Visit Date' },
    { key: 'engineTsn', label: 'Engine TSN' },
    { key: 'engineCsn', label: 'Engine CSN' },
  ],
  [{ key: 'remarks', label: 'Remarks', wide: true }],
];

function formatValue(value: ConcessionRecord[keyof ConcessionRecord]): string | undefined {
  if (value == null || value === '') return undefined;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  return VALUE_LABELS[String(value)] ?? String(value);
}

function engineLabel(record: ConcessionRecord): string | undefined {
  const parts = [record.engineModel, record.esn ? `ESN ${record.esn}` : undefined].filter(Boolean);
  return parts.length > 0 ? parts.join(' — ') : undefined;
}

/**
 * One concession / major-repair page row (DR-5166) — a row of an `OEM Concessions Statement` or
 * `Major Repair or Alteration Form` page: a header line with its role, engine and date, then each
 * populated field group as a label grid. Same card shell as the page's flight-log card. Renders
 * nothing when the record carries no value at all.
 */
export function ConcessionRecordCard({ title, record }: { title: string; record: ConcessionRecord }) {
  const groups = FIELD_GROUPS.map((fields) =>
    fields.flatMap((field) => {
      const value = formatValue(record[field.key]);
      return value === undefined ? [] : [{ ...field, value }];
    }),
  ).filter((entries) => entries.length > 0);
  const engine = engineLabel(record);
  const date = record.date ?? record.dateRaw;
  const hasHeader = record.role != null || engine != null || date != null;
  if (!hasHeader && groups.length === 0 && !record.noConcessions) return null;
  return (
    <div className="bg-white dark:bg-zinc-800 shadow dark:shadow-zinc-950/30 rounded-lg overflow-hidden border border-gray-200 dark:border-zinc-700">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-zinc-700 flex flex-wrap items-center gap-x-3 gap-y-1">
        <h2 className="text-sm font-medium text-gray-900 dark:text-zinc-100">{title}</h2>
        {record.role && <Badge color={ROLE_COLORS[record.role]}>{record.role}</Badge>}
        {(engine || date) && (
          <span className="text-sm text-gray-500 dark:text-zinc-400">{[engine, date].filter(Boolean).join(' · ')}</span>
        )}
      </div>
      {record.noConcessions && (
        <p className="px-4 py-4 text-sm font-medium text-gray-900 dark:text-zinc-100">No concessions declared</p>
      )}
      {groups.map((entries) => (
        <dl
          key={entries[0].key}
          className="px-4 py-4 grid grid-cols-3 gap-4 border-t border-gray-200 dark:border-zinc-700 first-of-type:border-t-0"
        >
          {entries.map(({ key, label, value, wide }) => (
            <div key={key} className={wide ? 'col-span-3' : undefined}>
              <dt className="text-sm font-medium text-gray-500 dark:text-zinc-400">{label}</dt>
              <dd className="mt-1 text-sm text-gray-900 dark:text-zinc-100 whitespace-pre-line">{value}</dd>
            </div>
          ))}
        </dl>
      ))}
    </div>
  );
}
